import { z } from "zod";
import type { CabinetState } from "../../../context/StoreContext";
import { type ObjectList, getShouldHaveBlindVertical } from "../data";
import { IdPrefix, generateId } from "../utils";
import { BACKGROUND_THICKNESS } from "./Background";
import { type CompartmentType, type HorizontalSide, getLeftCompartments } from "./Compartment";
import { DOOR_MARGIN, DOOR_THICKNESS } from "./Door";

export const VerticalSchema = z.object({
	id: z.string(),
	x: z.number(),
	y: z.number(),
	z: z.number(),
	width: z.number().max(1180).min(150),
	height: z.number(),
	depth: z.number(),
});

export type Vertical = z.infer<typeof VerticalSchema>;

export function verticalsFromCompartment(
	compartment: CompartmentType,
	compartments: ObjectList<CompartmentType>,
	thickness: number,
	width: number,
): Vertical[] {
	const verticals: Vertical[] = [];

	const shouldAddBlindVertical = getShouldHaveBlindVertical(compartment);
	const left = getLeftCompartments(compartment, [...compartments.values()], thickness);

	if (left.length > 1) {
		for (const c of left) {
			verticals.push({
				id: generateId(IdPrefix.Vertical),
				x: c.x + c.width,
				y: Math.max(c.y, compartment.y),
				z: c.z,
				width:
					c.y <= compartment.y
						? c.y + c.height - compartment.y
						: c.y + c.height < compartment.y + compartment.height
							? c.height
							: compartment.y + compartment.height - c.y,
				depth: c.depth,
				height: thickness,
			});
		}
	} else {
		verticals.push({
			id: generateId(IdPrefix.Vertical),
			x: compartment.x - thickness,
			y: compartment.y,
			z: compartment.z,
			width: compartment.height,
			depth: compartment.depth,
			height: thickness,
		});
	}

	if (compartment.x + compartment.width + thickness === width) {
		verticals.push({
			id: generateId(IdPrefix.Vertical),
			x: compartment.x + compartment.width,
			y: compartment.y,
			z: compartment.z,
			width: compartment.height,
			depth: compartment.depth,
			height: thickness,
		});
	}

	// Add blind vertical if compartment has double door
	if (shouldAddBlindVertical) {
		verticals.push({
			id: generateId(IdPrefix.Vertical),
			x: compartment.x + compartment.width / 2 - thickness / 2,
			y: compartment.y,
			z: compartment.z + BACKGROUND_THICKNESS,
			width: compartment.height,
			depth: compartment.depth - BACKGROUND_THICKNESS - DOOR_THICKNESS - DOOR_MARGIN,
			height: thickness,
		});
	}

	return verticals;
}

export function getNextVertical(
	vertical: Vertical,
	direction: HorizontalSide,
	state: CabinetState,
): undefined | Vertical {
	return [...state.verticals.values()].find((v) => {
		const isNextTo =
			direction === "top"
				? vertical.y + vertical.width + state.thickness === v.y
				: vertical.y === v.y + v.width + state.thickness;

		return vertical.x === v.x && isNextTo;
	});
}
